import type { RowData } from '@tanstack/react-table';
import type { Column } from '@tanstack/table-core';
import { cn } from 'cn';
import { useTranslation } from 'react-i18next';
import MarkMatches from '../../MarkMatches/MarkMatches';
import type { kkTableFeatures } from '../tableFeatures';
import type { FilterOption } from './filterOptions';

interface ImmediateCheckboxFilterOptionsProps<TData extends RowData> {
  column: Column<typeof kkTableFeatures, TData, unknown> | undefined;
  options: FilterOption<unknown>[];
  search: string;
  selectedValues: Set<string>;
  hasVisibleOptions: boolean;
  hasAllVisibleOptionsSelected: boolean;
  isSelectAllIndeterminate: boolean;
  onToggleOption: (value: string) => void;
  onToggleVisibleOptions: () => void;
}

interface ImmediateCheckboxFilterOptionProps<TData extends RowData> {
  column: Column<typeof kkTableFeatures, TData, unknown> | undefined;
  option: FilterOption<unknown>;
  search: string;
  selected: boolean;
  onToggle: (value: string) => void;
}

function ImmediateCheckboxFilterOption<TData extends RowData>({
  column,
  option,
  search,
  selected,
  onToggle,
}: ImmediateCheckboxFilterOptionProps<TData>) {
  if (!option.value) return null;

  const labelText =
    column?.columnDef.meta?.filterOptionLabel?.(option.original, column) ??
    option.value;
  const label =
    typeof column?.columnDef.meta?.renderFilterOption === 'function' ? (
      column.columnDef.meta.renderFilterOption(option.original, column)
    ) : (
      <MarkMatches text={labelText} search={search} />
    );

  if (label === null) return null;

  return (
    <li>
      <label
        className={cn(
          'ring-blue-20 has-focus-visible:bg-neutral-5 has-focus-visible:text-blue-60 hover:bg-neutral-5 has-focus-visible:font-semibold',
          'flex cursor-pointer items-center space-x-4 rounded px-4 py-1.5 has-focus-visible:ring-2',
        )}
      >
        <input
          type="checkbox"
          name={column?.id}
          value={option.value}
          checked={selected}
          onChange={() => {
            onToggle(option.value);
          }}
        />
        <span className="typ-body-xs line-clamp-1 block truncate">{label}</span>
      </label>
    </li>
  );
}

export function ImmediateCheckboxFilterOptions<TData extends RowData>({
  column,
  options,
  search,
  selectedValues,
  hasVisibleOptions,
  hasAllVisibleOptionsSelected,
  isSelectAllIndeterminate,
  onToggleOption,
  onToggleVisibleOptions,
}: ImmediateCheckboxFilterOptionsProps<TData>) {
  const { t } = useTranslation('ui');

  if (options.length <= 0) {
    return (
      <div className="typ-body-xs px-2 py-4 text-center text-neutral-100">
        {t('noSearchResults')}
      </div>
    );
  }

  return (
    <ol className="max-h-80 overflow-y-auto px-0.5 py-1 space-y-1">
      <li>
        <label
          className={cn(
            'ring-blue-20 has-focus-visible:bg-neutral-5 has-focus-visible:text-blue-60 hover:bg-neutral-5',
            'flex cursor-pointer items-center space-x-2 rounded px-2 py-1.5 has-focus-visible:ring-2',
          )}
        >
          <input
            ref={(node) => {
              if (!node) return;
              node.indeterminate = isSelectAllIndeterminate;
            }}
            type="checkbox"
            checked={hasAllVisibleOptionsSelected}
            disabled={!hasVisibleOptions}
            aria-label={t('toggleAll')}
            data-testid={`filter-select-all-${column?.id}`}
            onChange={onToggleVisibleOptions}
          />
          <span className="typ-body-xs line-clamp-1 block truncate">
            {t('toggleAll')}
          </span>
        </label>
      </li>
      {options.map((option) => (
        <ImmediateCheckboxFilterOption
          key={column?.id + option.value}
          column={column}
          option={option}
          search={search}
          selected={selectedValues.has(option.value)}
          onToggle={onToggleOption}
        />
      ))}
    </ol>
  );
}
